// Types
import { Point } from '../../../common/maths/geometry/pointTypes';
import { getPagePositionFromClientPosition } from '../domUtil';
import { createGetRelativePositionFromEventFn } from './domEventUtil';

/**
 * Determines the size, in pixels, of the current touch event if it has two touch points.
 * I.e. The distance between the two touch points.
 */
export const getGestureSize = (event: TouchEvent): number => {
    const touchA = event.touches.item(0);
    const touchB = event.touches.item(1);

    if (!touchA || !touchB) return 0;

    const dx = touchB.clientX - touchA.clientX;
    const dy = touchB.clientY - touchA.clientY;

    return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Determines the position between the two touch points.
 */
export const getGestureCentre = (event: TouchEvent): Point => {
    const touchA = event.touches.item(0);
    const touchB = event.touches.item(1);

    if (!touchA || !touchB) return { x: 0, y: 0 };

    return {
        x: (touchB.clientX + touchA.clientX) / 2,
        y: (touchB.clientY + touchA.clientY) / 2,
    };
};

/**
 * Gets the client position (the position relative to the browser's visible viewport) of the touch event.
 * Assumes that the touch event has only one touch point.
 */
const getClientPositionFromTouchEvent = (event: TouchEvent) => {
    const touch = event.touches?.item?.(0);

    // This shouldn't happen, but Safari supports pageX and pageY on TouchEvents, and we were using these
    //  prior to supporting Android devices, so we'll fall back to this if needed.
    if (!touch) return { x: event.pageX || 0, y: event.pageY || 0 };

    return { x: touch.clientX, y: touch.clientY };
};

/**
 * Gets the page position (the position relative to the entire document) of the touch event.
 */
const getPagePositionFromTouchEvent = (event: TouchEvent) => {
    const clientPosition = getClientPositionFromTouchEvent(event);
    return getPagePositionFromClientPosition(clientPosition);
};

/**
 * Gets the relative position (in relation to the position of another element) of the touch event.
 *
 * E.g. We use this when performing a marquee selection, to know the relative offset to the parent (the canvas
 * or the unsorted notes, for example), so that we can draw the selection rectangle offset from the top left
 * of that element.
 */
export const getRelativePositionFromTouchEvent = createGetRelativePositionFromEventFn(getPagePositionFromTouchEvent);
